import React, { useCallback, useEffect, useState } from "react";
import { searchMaintenanceOfficeUsers } from "../../api/maintenanceApi";
import { GlobalButton, GlobalTextField } from "../../../../global/forms/GlobalFormFields";

/**
 * Search-and-pick an office-role staff member (same UX pattern as call logs “Handled by”).
 * `userId` / `displayName` are controlled from the parent row state.
 */
export default function OfficeStaffAssignTypeahead({
  userId = "",
  displayName = "",
  onChange,
  label = "Assign to (office staff)",
  required = false,
  error = "",
  disabled = false,
}) {
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [showUserResults, setShowUserResults] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const hasSelection = Boolean(String(userId || "").trim());

  useEffect(() => {
    if (hasSelection) {
      setUserSearch("");
      setUserResults([]);
      setShowUserResults(false);
      return;
    }

    const t = setTimeout(async () => {
      if (userSearch.trim().length < 2) {
        setUserResults([]);
        setShowUserResults(false);
        return;
      }
      setSearchingUsers(true);
      try {
        const rows = await searchMaintenanceOfficeUsers(userSearch.trim());
        setUserResults(rows);
        setShowUserResults(true);
      } catch {
        setUserResults([]);
        setShowUserResults(false);
      } finally {
        setSearchingUsers(false);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [userSearch, hasSelection]);

  const handleUserSelect = useCallback(
    (user) => {
      onChange?.(String(user.id), user.name || "");
      setShowUserResults(false);
      setUserSearch("");
      setUserResults([]);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange?.("", "");
    setUserSearch("");
    setUserResults([]);
    setShowUserResults(false);
  }, [onChange]);

  const labelNode = (
    <span className="gff-label">
      {label}
      {required ? <span style={{ color: "#dc2626" }}> *</span> : null}
    </span>
  );

  return (
    <div className="gff-field gff-field--full" style={{ position: "relative" }}>
      {labelNode}

      {hasSelection ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0.75rem 1rem",
            background: "var(--accent-soft, #f0f9ff)",
            border: "1px solid var(--accent-muted, #bae6fd)",
            borderRadius: "0.5rem",
            marginTop: "0.5rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "var(--accent, #0ea5e9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            >
              {String(displayName || "?").charAt(0).toUpperCase()}
            </div>
            <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
              {displayName || `User #${userId}`}
            </div>
          </div>
          <GlobalButton variant="ghost" size="sm" onClick={handleClear} disabled={disabled}>
            Change
          </GlobalButton>
        </div>
      ) : (
        <>
          <GlobalTextField
            label=""
            placeholder="Type at least 2 characters to search office staff…"
            value={userSearch}
            onChange={setUserSearch}
            error={error}
            disabled={disabled}
          />
          {searchingUsers ? (
            <div
              style={{
                position: "absolute",
                right: "1rem",
                top: "2.75rem",
                fontSize: "0.75rem",
                color: "var(--accent, #0ea5e9)",
              }}
            >
              Searching…
            </div>
          ) : null}
          {showUserResults && userResults.length > 0 ? (
            <div
              style={{
                position: "absolute",
                backgroundColor: "#fff",
                border: "1px solid var(--border, #e2e8f0)",
                width: "100%",
                zIndex: 20,
                maxHeight: "200px",
                overflowY: "auto",
                borderRadius: "0.5rem",
                boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                marginTop: "0.25rem",
              }}
            >
              {userResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => handleUserSelect(u)}
                  style={{
                    display: "flex",
                    width: "100%",
                    textAlign: "left",
                    padding: "0.75rem 1rem",
                    cursor: "pointer",
                    border: "none",
                    borderBottom: "1px solid var(--border, #f1f5f9)",
                    fontSize: "0.875rem",
                    background: "#fff",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fff";
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>{u.name}</div>
                    {u.user_code ? (
                      <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{u.user_code}</div>
                    ) : null}
                  </div>
                  <span style={{ fontSize: "0.75rem", color: "var(--accent, #0ea5e9)", fontWeight: 600 }}>
                    Select
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {showUserResults && userResults.length === 0 && !searchingUsers && userSearch.trim().length >= 2 ? (
            <div
              style={{
                position: "absolute",
                backgroundColor: "#fff",
                border: "1px solid var(--border, #e2e8f0)",
                width: "100%",
                zIndex: 20,
                padding: "0.75rem 1rem",
                borderRadius: "0.5rem",
                marginTop: "0.25rem",
                fontSize: "0.875rem",
                color: "#64748b",
              }}
            >
              No office staff found
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
